import React, { useEffect, useRef, forwardRef, useState, useImperativeHandle } from 'react';
import { Stage, Layer, Rect, Line, Group } from 'react-konva';

const PuzzleCanvas = forwardRef(({ stage, ...props }, ref) => {
	const setLevel = (level) => {
		switch(level) {
			case 'hard':
				return 5;

			case 'normal':
				return 4;

			case 'easy':
				return 3;
		}

		return 3;
	};

	const level = stage?.puzzle_level ?? 'easy';
	const display_help = stage?.display_legend ?? true;
	const display_outter_border = stage?.display_outter_border ?? true;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const default_size = 600;

	const line_color = '0, 0, 0';

	const fillPatternImage = props?.indexed_images?.puzzle_image?.fillPatternImage;
	const image_w = props?.indexed_images?.puzzle_image?.image_w ?? 100;
	const image_h = props?.indexed_images?.puzzle_image?.image_h ?? 100;
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const help_duration = 2;	// in sec
	const delay_to_success = 0.5; // in seconds

	const columns = useRef(setLevel(level));
	const positions_items = useRef([]);
	const refs = useRef([]);
	const items = useRef([]);
	const intersectors = useRef([]);
	const size = useRef(default_size);
	const is_dragging = useRef(false);
	const is_playable = useRef(false);

	const size_piece_w = image_w / columns.current;
	const size_piece_h = image_h / columns.current;

	const number_of_items = () => Math.pow(columns.current, 2);
	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	/* SOUNDS - ini */
	const startMovePlay = () => props.sound_fx.move_in.play();
	const endMovePlay = () => props?.sound_fx?.move_out.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();
	/* SOUNDS - end */

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		window.onresize = () => {
			gameInit(true);
		}

		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);
	
	useEffect(() => {
		if(fillPatternImage == undefined) return;
		columns.current = setLevel(level);
		resetGame();
		gameInit();
	}, [
		stage.puzzle_level,
		fillPatternImage,
	]);

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			if(success_game === false) failPlay();

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					cb(success_game);
				}, (delay_to_success * 1000));
			});
		},

		showHelp() {
			if(!display_help) return;
			toggleHelp();
		},
	}), []);

	const toggleHelp = () => {
		refs.current['help'].setAttrs({
			y:0,
		});

		refs.current['layer'].draw();

		refs.current['help_bar'].to({
			scaleX:0,
			duration:help_duration,
			onFinish:() => {
				refs.current['help'].setAttrs({
					y:size.current + 10,
				});

				refs.current['help_bar'].setAttrs({
					scaleX:1,
				});
	
				refs.current[`layer`].draw();
			},
		});
	};

	const resetGame = () => {
		setFullLoaded(false);

		[...Array(number_of_items()).keys()].map((index) => {
			if(refs.current[`part_${index}`] != undefined) {
				refs.current[`part_${index}`].clearCache();
			}
		});

		gameInit(true);
	};

	const gameInit = (from_resize = false) => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = _size;

				if(!from_resize) {
					calcSquares();
					shuffle();

					setTimeout(() => {
						generateCache();
						mountGame();
					}, 300);
				}

				setFullLoaded(true);
			});
		}
	};

	const generateCache = () => {
		if(refs.current['bg_img'] != undefined) {
			refs.current['bg_img'].cache(default_cache_props);
		}

		if(refs.current['help_img'] != undefined) {
			refs.current['help_img'].cache(default_cache_props);
		}

		[...Array((number_of_items())).keys()].map((index) => {
			if(refs.current[`part_${index}`] != undefined) {
				refs.current[`part_${index}`].cache(default_cache_props);
			}
		});
	};

	const calcSquares = () => {
		const _items = [];

		for(let i = 0; i < number_of_items(); i++) {
			_items.push({
				position:i,
			});
		}

		items.current = _items;

		const size_part = size.current / columns.current;

		for(var pos = 0; pos < number_of_items(); pos++) {
			const ini_pos_x = Math.floor(pos % columns.current) * size_part;
			const ini_pos_y = Math.floor(pos / columns.current) * size_part;
			positions_items.current.push({
				x:ini_pos_x,
				y:ini_pos_y,
			});
		}
	};

	const shuffle = () => {
		const _items = [ ...items.current ];
		const threshold_solved_percentage = 15; //2 Pieces in Easy, 3 pieces in Medium, 4 Pieces in Hard
		if(_items.length > 0) {
			let currentIndex = _items.length;
			let temporaryValue;
			let randomIndex;
			// While there remain elements to shuffle...
			while(0 !== currentIndex) {
				// Pick a remaining element...
				randomIndex = Math.floor(Math.random() * currentIndex);
				currentIndex -= 1;

				// And swap it with the current element.
				temporaryValue = _items[currentIndex];
				_items[currentIndex] = _items[randomIndex];
				_items[randomIndex] = temporaryValue;
			}

			let correct_pieces_in_puzzle = 0;
			_items.map((item, position) => {
				if(item.position == position) correct_pieces_in_puzzle++;
			});

			const very_very_solved_in_shuffle = ((correct_pieces_in_puzzle / _items.length * 100) > threshold_solved_percentage) ? true : false;

			items.current = _items;

			if(very_very_solved_in_shuffle) {
				shuffle();
			}
		}
	};

	const checkSolved = () => {
		let is_solved = true;
		[...Array(number_of_items()).keys()].map((index) => {
			if(refs.current[`part_${index}`].attrs.correct === false) is_solved = false;
		});
		return is_solved;
	}

	const isSolved = () => {
		let is_solved = checkSolved();

		if(is_solved) {
			successPlay();
			props.onScore(100);

			if(props.onEnded != undefined) props.onEnded(true);
		}

		return is_solved;
	};

	const mountGame = () => {
		let finished = 0;

		setTimeout(() => {
			if(refs.current['all_lines'] == undefined) return;

			refs.current['all_lines'].to({
				opacity:1,
				duration:1,
				easing:Konva.Easings.BounceEaseOut,
			});
		}, 600);

		items.current.map((value, index) => {
			setTimeout(() => {
				if(refs.current[`part_${value.position}`] == undefined) return;

				refs.current[`part_${value.position}`].to({
					scaleX:1,
					scaleY:1,
					duration:0.5,
					easing:Konva.Easings.BounceEaseOut,
					onFinish:() => {
						finished++;

						if(finished == items.current.length) {
							is_playable.current = true;

							if(refs.current['whole_tapper'] != undefined) {
								refs.current['whole_tapper'].setAttrs({
									y:size.current + 10,
								});
							}

							props.startTimer();
						}
					},
				});
			}, (100 * index));
		});
	};

	const unmountBoardAnimation = (is_solved = true, cb) => {
		let finished = 0;
		is_playable.current = false;

		refs.current['whole_tapper'].setAttrs({
			y:0,
		});

		if(is_solved === false) {
			items.current.map((value, index) => {
				setTimeout(() => {
					if(refs.current[`part_${value.position}`] == undefined) return;
	
					refs.current[`part_${value.position}`].to({
						scaleX:0.8,
						scaleY:0.8,
						saturation:-50,
						duration:0.3,
						easing:Konva.Easings.StrongEaseIn,
						onFinish:() => {
							cb();
						},
					});
				}, (30 * index));
			});

			return;
		}

		items.current.map((value, index) => {
			setTimeout(() => {
				if(refs.current[`part_${value.position}`] == undefined) return;

				refs.current[`part_${value.position}`].to({
					rotation:180,
					scaleX:0.8,
					scaleY:0.8,
					duration:0.5,
					easing:Konva.Easings.StrongEaseIn,
					onFinish:() => {
						if(refs.current[`part_${value.position}`] == undefined) return;

						refs.current[`part_${value.position}`].to({
							rotation:360,
							duration:0.5,
							scaleX:1,
							scaleY:1,
							easing:Konva.Easings.StrongEaseOut,
							onFinish:() => {
								finished++;

								if(finished == items.current.length) {
									if(refs.current['all_lines'] == undefined) return;

									refs.current[`bg`].opacity(0);

									refs.current['all_lines'].to({
										opacity:0,
										duration:2,
										easing:Konva.Easings.StrongEaseInOut,
										onFinish:() => {
											setTimeout(() => {
												cb();
											}, 2000);
										},
									});
								}
							},
						});
					},
				});
			}, (100 * index));
		});
	};

	const semihideOthers = (exception, direction = 'in') => {
		refs.current['all_lines'].to({
			opacity:(direction == 'in') ? 0.5 : 1,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current[`part_${exception}_stroke`].to({
			opacity:(direction == 'in') ? 1 : 0,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});
		
		items.current.map((piece) => {
			if(direction == 'in' && piece.position == exception) return;

			refs.current[`part_${piece.position}`].to({
				opacity:(direction == 'in') ? 0.7 : 1,
				duration:0.2,
				easing:Konva.Easings.StrongEaseInOut,
			});
		});
	};

	const handleStart = (e) => {
		if(is_playable.current === false || is_dragging.current === true) return;

		is_dragging.current = true;

		refs.current[`areas`].x(0);

		const target = e.target;

		semihideOthers(target.attrs.actual_position);

		target.zIndex(items.current.length + 3);
		target.to({
			scaleX:1.2,
			scaleY:1.2,
			duration:0.2,
			easing:Konva.Easings.BounceEaseOut,
		});

		startMovePlay();
	};

	const handleMove = (e) => {
		if(is_dragging.current === false) return;

		intersectors.current = [];

		const target = e.target;
		[...Array(number_of_items()).keys()].map((index) => {
			const group = refs.current[`area_part_${index}`];

			const targetRect = target.getClientRect();

			if(haveIntersection(group.getClientRect(), targetRect)) {
				intersectors.current.push(group.attrs.this_pos);
			}
		});
	};

	const handleEnd = (e) => {
		const target = e.target;

		semihideOthers(target.attrs.actual_position, 'out');

		let destiny_index = parseInt(target.attrs.name.split("_")[1]);
		if(intersectors.current.length > 0) destiny_index = intersectors.current[0];

		refs.current['areas'].x(1.1 * size.current);

		const actual_index = parseInt(target.attrs.name.split("_")[1]);
		const next_piece = refs.current['layer'].find(`.item_${destiny_index}`)[0];

		const actual_ok = (destiny_index == target.attrs.this_pos) ? true : false;
		target.setAttrs({
			name:`item_${destiny_index}`,
			correct:actual_ok,
		});

		target.to({
			x:positions_items.current[destiny_index].x + size_portion / 2,
			y:positions_items.current[destiny_index].y + size_portion / 2,
			scaleX:1,
			scaleY:1,
			duration:0.3,
			easing:Konva.Easings.BounceEaseOut,
			onFinish:() => {
				is_dragging.current = false;

				target.setAttrs({
					name:`item_${destiny_index}`,
					zIndex:2,
					correct:actual_ok,
				});
			}
		});

		endMovePlay();

		if(destiny_index != actual_index) {
			const next_ok = (actual_index == next_piece.attrs.this_pos) ? true : false;
			next_piece.setAttrs({
				name:`item_${actual_index}`,
				correct:next_ok,
			});
			next_piece.to({
				x:positions_items.current[actual_index].x + size_portion / 2,
				y:positions_items.current[actual_index].y + size_portion / 2,
				duration:0.3,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					next_piece.setAttrs({
						name:`item_${actual_index}`,
						zIndex:3,
						correct:next_ok,
					});

					isSolved();
				},
			});

			
			if((!is_builder || is_test) && props.doHit != undefined) props.doHit(checkSolved());
		}
	};

	const haveIntersection = (r1, r2) => {
		return !(
			r2.x > r1.x + r1.width ||
			r2.x + r2.width < r1.x ||
			r2.y > r1.y + r1.height ||
			r2.y + r2.height < r1.y
		);
	};

	const size_portion = size.current / columns.current;

	if(!full_loaded) return null;

	const canvas_scale = size.current / default_size;

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			ref={(node) => { refs.current['stage'] = node; }}
			class={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer ref={(node) => { refs.current['layer'] = node; }}>
				<Rect
					ref={(node) => { refs.current['bg_img'] = node; }}
					width={size.current}
					height={size.current}
					fillPatternImage={fillPatternImage}
					fillPatternRepeat="no-repeat"
					fillPatternScaleX={size.current / image_w}
					fillPatternScaleY={size.current / image_h}
				/>

				<Rect
					ref={(node) => { refs.current['bg'] = node; }}
					fill='white'
					width={size.current}
					height={size.current}
					opacity={1}
				/>

				{items.current.map((value, index) => {
					const x_ini = Math.floor(index % columns.current) * size_portion;
					const y_ini = Math.floor(index / columns.current) * size_portion;

					const offset_x = Math.floor(value.position % columns.current) * size_piece_w;
					const offset_y = Math.floor(value.position / columns.current) * size_piece_h;

					return (
						<Group
							ref={(node) => { refs.current[`part_${value.position}`] = node; }}
							key={`item_${index}`}
							name={`item_${index}`}
							draggable={true}
							x={x_ini + size_portion / 2}
							y={y_ini + size_portion / 2}
							onDragStart={(e) => handleStart(e)}
							onDragMove={(e) => handleMove(e)}
							onDragEnd={(e) => handleEnd(e)}
							onMouseEnter={() => { document.body.style.cursor = 'move'; }}
							onMouseLeave={() => { document.body.style.cursor = 'default'; }}
							scaleX={0}
							scaleY={0}
							offsetX={size_portion / 2}
							offsetY={size_portion / 2}
							this_pos={value.position}
							width={size_portion}
							height={size_portion}
							correct={(value.position == index) ? true : false}
							actual_position={value.position}
							filters={[Konva.Filters.HSL]}
						>
							<Rect
								ref={(node) => { refs.current[`part_${value.position}_stroke`] = node; }}
								width={size_portion}
								height={size_portion}
								fill='black'
								stroke='black'
								strokeWidth={4}
								opacity={0}
							/>

							<Rect
								width={size_portion}
								height={size_portion}
								fillPatternImage={fillPatternImage}
								fillPatternRepeat="no-repeat"
								fillPatternScaleX={size.current / image_w}
								fillPatternScaleY={size.current / image_h}
								fillPatternOffsetX={offset_x}
								fillPatternOffsetY={offset_y}
							/>
						</Group>
					)
				})}

				<Group
					ref={(node) => { refs.current['whole_tapper'] = node; }}
					y={0}
				>
					<Rect
						width={size.current}
						height={size.current}
						fill='white'
						opacity={0}
					/>
				</Group>

				<Group
					ref={(node) => { refs.current['help'] = node; }}
					y={size.current + 10}
				>
					<Rect
						ref={(node) => { refs.current['help_img'] = node; }}
						width={size.current}
						height={size.current}
						fillPatternImage={fillPatternImage}
						fillPatternRepeat="no-repeat"
						fillPatternScaleX={size.current / image_w}
						fillPatternScaleY={size.current / image_h}
					/>

					<Rect
						fill='black'
						width={size.current}
						opacity={0.7}
						height={9}
					/>

					<Rect
						ref={(node) => { refs.current['help_bar'] = node; }}
						fill='white'
						width={size.current - 4}
						scaleX={1}
						x={2}
						y={2}
						height={5}
					/>
				</Group>

				<Group
					ref={(node) => { refs.current['all_lines'] = node; }}
					opacity={0}
				>
					{[...Array(columns.current + 1).keys()].map((i) => {
						return (
							<Line
								key={`line_v-${i}`}
								points={[0, 0, 0, size.current]}
								x={i * size_portion}
								stroke={`rgba(${line_color}, 1)`}
								strokeWidth={1 / canvas_scale}
								opacity={0.4}
							/>
						)
					})}

					{[...Array(columns.current + 1).keys()].map((i) => {
						return (
							<Line
								key={`line_h-${i}`}
								points={[0, 0, size.current, 0]}
								y={i * size_portion}
								stroke={`rgba(${line_color}, 1)`}
								strokeWidth={1 / canvas_scale}
								opacity={0.4}
							/>
						)
					})}
				</Group>

				<Group
					ref={(node) => { refs.current['areas'] = node; }}
					x={1.1 * size.current}
					opacity={0}
				>
					{items.current.map((value, index) => {
						const x_ini = Math.floor(index % columns.current) * size_portion;
						const y_ini = Math.floor(index / columns.current) * size_portion;

						return (
							<Rect
								ref={(node) => { refs.current[`area_part_${index}`] = node; }}
								key={`area_item-${index}`}
								x={x_ini + 0.45 * size_portion}
								y={y_ini + 0.45 * size_portion}
								width={0.1 * size_portion}
								height={0.1 * size_portion}
								fill='black'
								this_pos={index}
							/>
						)
					})}
				</Group>
			</Layer>
		</Stage>
	);
});

export default PuzzleCanvas;